/**
 * 图片加载逻辑
 */
import { MAX_CONCURRENT_LOAD_TASKS } from '../utils/constants';

/**
 * 判断是否为审核目标图片
 * @param {*} image - 图片对象
 * @returns {boolean}
 */
function isAuditTarget(image) {
  return Boolean(image && typeof image === 'object' && image.im);
}

export function useImageLoader() {
  return {
    /**
     * 开始加载图片队列
     * @param {Array} images - 图片数组
     * @param {object} options - 选项
     * @param {Function} options.onUpdate - 单张图片加载完成回调 (image, imageObject, isError) => void
     * @param {Function} options.onProgress - 进度更新回调 (finishedCount, totalCount) => void
     * @param {Function} options.onStatusChange - 状态文本变化回调 (text) => void
     * @param {Function} options.onEnd - 全部加载完成回调 () => void
     * @param {object} tokenRef - 用于取消加载的 token 引用 { current: number }
     * @param {Function} isTokenValid - 判断 token 是否有效的函数 (currentToken) => boolean
     * @returns {object} { loadToken }
     */
    startLoadQueue(
      images,
      { onUpdate, onProgress, onStatusChange, onEnd },
      tokenRef,
      isTokenValid
    ) {
      const loadQueue = Array.isArray(images) ? images.map((image, index) => ({ image, index })) : [];
      const loadToken = Date.now();
      tokenRef.current = loadToken;

      if (!loadQueue.length) {
        if (typeof onEnd === 'function') {
          onEnd();
        }
        return { loadToken };
      }

      let runningCount = 0;
      let nextTaskIndex = 0;
      let loadFinishedCount = 0;
      const loadTotalCount = loadQueue.length;

      const canContinue = () => isTokenValid(loadToken);

      const finalizeTask = () => {
        if (!canContinue()) {
          return;
        }

        runningCount = runningCount > 0 ? runningCount - 1 : 0;
        loadFinishedCount++;

        if (typeof onProgress === 'function') {
          onProgress(loadFinishedCount, loadTotalCount);
        }

        if (loadFinishedCount === loadTotalCount && runningCount === 0 && nextTaskIndex >= loadQueue.length) {
          if (typeof onEnd === 'function') {
            onEnd();
          }
          return;
        }

        pumpQueue();
      };

      const handleLoadComplete = (image, imageObject, isError) => {
        if (!canContinue()) {
          return;
        }

        if (isAuditTarget(image)) {
          if (isError) {
            image.orisize = [300, 300];
          } else {
            image.orisize = [imageObject.width, imageObject.height];
          }
        }

        if (typeof onUpdate === 'function') {
          onUpdate(image, imageObject, isError);
        }
        finalizeTask();
      };

      const runLoadTask = (image) => {
        if (typeof onStatusChange === 'function') {
          onStatusChange('正在加载图片');
        }

        const imageUrl = isAuditTarget(image) ? image.im : image;
        const im = new Image();

        im.onload = function () {
          handleLoadComplete(image, this, false);
        };

        im.onerror = function () {
          handleLoadComplete(image, this, true);
        };

        im.src = imageUrl;
      };

      const pumpQueue = () => {
        if (!canContinue()) {
          return;
        }

        while (runningCount < MAX_CONCURRENT_LOAD_TASKS && nextTaskIndex < loadQueue.length) {
          const task = loadQueue[nextTaskIndex];
          nextTaskIndex++;
          runningCount++;
          runLoadTask(task.image);
        }
      };

      pumpQueue();

      return { loadToken };
    }
  };
}
